import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { 
  Share2, 
  Link2, 
  Twitter, 
  Linkedin, 
  Facebook,
  MessageCircle,
  Mail,
  Code,
  QrCode,
  Check,
  Copy,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

export const ShareButton = ({ 
  url, 
  title, 
  description,
  type = 'mindmap', // 'mindmap' | 'node' | 'profile'
  showEmbed = true,
  variant = 'default', // 'default' | 'icon'
}) => {
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || 'Check out this mindmap on Clarities';
  const shareDescription = description || 'Collaborative knowledge sharing platform';

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedDescription = encodeURIComponent(shareDescription);

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const openShareWindow = (url) => {
    window.open(url, '_blank', 'width=600,height=400');
  };

  // Generate embed URL (uses dedicated embed route)
  const getEmbedUrl = () => {
    const urlObj = new URL(shareUrl);
    // Convert /q/questionId to /embed/questionId
    const pathParts = urlObj.pathname.split('/');
    if (pathParts[1] === 'q') {
      pathParts[1] = 'embed';
      urlObj.pathname = pathParts.join('/');
    }
    return urlObj.toString();
  };

  const embedUrl = getEmbedUrl();
  const embedCode = `<iframe 
  src="${embedUrl}" 
  width="100%" 
  height="600" 
  frameborder="0" 
  style="border: 1px solid #e2e8f0; border-radius: 8px;"
  title="${shareTitle}"
></iframe>`;

  const copyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      toast.success('Embed code copied!');
    } catch (err) {
      toast.error('Failed to copy embed code');
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `clarities-${type}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === 'icon' ? (
            <Button 
              variant="ghost" 
              size="icon"
              className="text-slate-500 hover:text-slate-700"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          ) : (
            <Button 
              variant="outline" 
              className="gap-2"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={copyToClipboard} data-testid="copy-link">
            {copied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Link2 className="w-4 h-4 mr-2" />}
            {copied ? 'Copied!' : 'Copy link'}
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={() => openShareWindow(shareLinks.twitter)} data-testid="share-twitter">
            <Twitter className="w-4 h-4 mr-2" />
            Share on Twitter
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openShareWindow(shareLinks.linkedin)} data-testid="share-linkedin">
            <Linkedin className="w-4 h-4 mr-2" />
            Share on LinkedIn
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openShareWindow(shareLinks.facebook)} data-testid="share-facebook">
            <Facebook className="w-4 h-4 mr-2" />
            Share on Facebook
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openShareWindow(shareLinks.whatsapp)} data-testid="share-whatsapp">
            <MessageCircle className="w-4 h-4 mr-2" />
            Share on WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.location.href = shareLinks.email} data-testid="share-email">
            <Mail className="w-4 h-4 mr-2" />
            Share via Email
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {showEmbed && (
            <DropdownMenuItem onClick={() => setShowEmbedDialog(true)} data-testid="embed-code">
              <Code className="w-4 h-4 mr-2" />
              Embed code
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setShowQRDialog(true)} data-testid="qr-code">
            <QrCode className="w-4 h-4 mr-2" />
            QR Code
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Embed Dialog */}
      <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
        <DialogContent className="max-w-lg" data-testid="embed-dialog">
          <DialogHeader>
            <DialogTitle>Embed this {type}</DialogTitle>
            <DialogDescription>
              Copy the code below to embed this {type} on your website
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm font-mono overflow-x-auto">
              <pre className="whitespace-pre-wrap break-all">{embedCode}</pre>
            </div>
            <Button 
              onClick={copyEmbedCode} 
              className="w-full mt-4 gap-2"
              data-testid="copy-embed-btn"
            >
              <Copy className="w-4 h-4" />
              Copy Embed Code
            </Button>
            
            <div className="mt-6">
              <p className="text-sm text-slate-500 mb-3">Preview:</p>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="aspect-video bg-white rounded border border-slate-200 flex items-center justify-center text-slate-400 text-sm">
                  Embedded {type} will appear here
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>
              Scan to open this {type}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg border border-slate-200">
              <QRCode
                id="qr-code-svg"
                value={shareUrl}
                size={200}
                level="H"
              />
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center break-all max-w-full">
              {shareUrl}
            </p>
            <Button 
              onClick={downloadQR} 
              className="w-full mt-4 gap-2"
              data-testid="download-qr-btn"
            >
              <Download className="w-4 h-4" />
              Download QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Inline share buttons for quick sharing
export const InlineShareButtons = ({ url, title }) => {
  const shareUrl = url || window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title || 'Check out this on Clarities');

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
  };

  const openShare = (url) => {
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => openShare(shareLinks.twitter)}
        className="w-8 h-8 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        title="Share on Twitter"
      >
        <Twitter className="w-4 h-4" />
      </button>
      <button
        onClick={() => openShare(shareLinks.linkedin)}
        className="w-8 h-8 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        title="Share on LinkedIn"
      >
        <Linkedin className="w-4 h-4" />
      </button>
      <button
        onClick={() => openShare(shareLinks.facebook)}
        className="w-8 h-8 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        title="Share on Facebook"
      >
        <Facebook className="w-4 h-4" />
      </button>
      <button
        onClick={() => openShare(shareLinks.whatsapp)}
        className="w-8 h-8 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
        title="Share on WhatsApp"
      >
        <MessageCircle className="w-4 h-4" />
      </button>
    </div>
  );
};

